import { FC, useMemo, useState } from 'react';

import { COMPARISON_BAR_CHART_COLORS } from './constants';
import { CustomComparisonBar } from './CustomBar';
import { CustomComparisonTick } from './CustomComparisonTick';
import { CustomComparisonTooltip } from './CustomComparisonTooltip';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Skeleton } from '@/components';
import { CHARTS_TEST_IDS } from '@/constants/data-testids';

import { Card, CardContent } from '../../atoms/Card/Card';

const TICKS = [0, 25, 50, 75, 100];

export type ComparisonBarItem = {
  name: string;
  currentValue: number;
  previousValue: number;
  currentName: string;
  previousName: string;
};

type BarsColors = {
  currentColor: string;
  previousColor: string;
};

type ComparisonBarChartProps = {
  title: string;
  xKey: string;
  yKey: string;
  data: ComparisonBarItem[];
  size?: 'small' | 'medium' | 'large';
  variant?: 'success' | 'declined';
  isEditCellSkeleton?: boolean;
};

const yAxisWidths = {
  small: 120,
  medium: 164,
  large: 240,
};

const createCustomComparisonTick = (size: string) =>
  function CustomComparisonTickWrapper(props: any) {
    return <CustomComparisonTick {...props} size={size} />;
  };

const createCustomComparisonBar = (
  variant: string,
  barsColor: BarsColors,
  hoveredIndex: number | null,
  setHoveredIndex: (index: number | null) => void,
  isEditCellSkeleton?: boolean
) =>
  function CustomComparisonBarWrapper(props: any) {
    return (
      <CustomComparisonBar
        {...props}
        variant={variant}
        barsColor={barsColor}
        hoveredIndex={hoveredIndex}
        setHoveredIndex={setHoveredIndex}
        isEditCellSkeleton={isEditCellSkeleton}
      />
    );
  };

export const ComparisonBarChart: FC<ComparisonBarChartProps> = ({
  title,
  data,
  size = 'medium',
  xKey,
  yKey,
  variant = 'success',
  isEditCellSkeleton = false,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const containerHeight = 50 * data.length;
  const yAxisWidth = yAxisWidths[size] || yAxisWidths.medium;
  const barKey = variant === 'success' ? 'currentValue' : 'previousValue';

  const barsColor: BarsColors = useMemo(() => {
    switch (variant) {
      case 'success':
        return {
          currentColor: COMPARISON_BAR_CHART_COLORS.SUCCESS.CURRENT,
          previousColor: COMPARISON_BAR_CHART_COLORS.SUCCESS.PREV,
        };
      case 'declined':
        return {
          currentColor: COMPARISON_BAR_CHART_COLORS.DECLINED.CURRENT,
          previousColor: COMPARISON_BAR_CHART_COLORS.DECLINED.PREV,
        };
    }
  }, [variant]);

  const SkeletonXAxisTick = ({ x, y }: { x: number | string; y: number | string }) => {
    return (
      <g transform={`translate(${x}, ${y})`}>
        <foreignObject x={-10} y={0} width={20} height={20}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Skeleton className="w-4 h-3" />
          </div>
        </foreignObject>
      </g>
    );
  };
  const SkeletonTick = ({ x, y }: { x: number | string; y: number | string }) => {
    return (
      <g transform={`translate(${x}, ${y})`}>
        <foreignObject x={-yAxisWidth + 10} y={-10} width={yAxisWidth - 20} height={20}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Skeleton className="w-full h-4" />
          </div>
        </foreignObject>
      </g>
    );
  };

  return (
    <Card
      data-testid={CHARTS_TEST_IDS.COMPARISON_CHART}
      className="p-4 border-custom-warm-grey-150"
    >
      <CardContent>
        <div className="flex gap-3  justify-between">
          <p className="text-body-14-semibold !text-custom-warm-grey-700 mb-6">{title}</p>
          <div className="flex items-center gap-3 h-fit">
            <div className="flex shrink-0 gap-2">
              <svg width={12} height={12}>
                <circle cx="6" cy="6" r="6" fill={barsColor.previousColor} />
              </svg>
              <p className="text-body-12-regular !text-custom-warm-grey-700">
                {isEditCellSkeleton ? (
                  <Skeleton className="w-[134px] h-[12px]" />
                ) : (
                  'Previous quarter'
                )}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <svg width={12} height={12}>
                <circle cx="6" cy="6" r="6" fill={barsColor.currentColor} />
              </svg>
              <p className="text-body-12-regular !text-custom-warm-grey-700">
                {' '}
                {isEditCellSkeleton ? (
                  <Skeleton className="w-[81px] h-[12px]" />
                ) : (
                  'Current quarter'
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full h-64">
          <ResponsiveContainer width="100%" height={containerHeight}>
            <BarChart layout="vertical" data={data}>
              <CartesianGrid strokeDasharray="12 12" stroke="#F0EEED" horizontal={false} />
              {isEditCellSkeleton ? (
                <XAxis tickLine={false} axisLine={false} type="number" tick={SkeletonXAxisTick} />
              ) : (
                <XAxis
                  orientation="bottom"
                  dataKey={yKey}
                  type="number"
                  axisLine={false}
                  tickLine={false}
                  mirror={false}
                  ticks={TICKS}
                  tick={
                    isEditCellSkeleton
                      ? SkeletonTick
                      : {
                          fontSize: 12,
                          letterSpacing: '-0.01em',
                          color: '#A8A29D',
                        }
                  }
                />
              )}
              <YAxis
                axisLine={false}
                tickLine={false}
                mirror={false}
                width={yAxisWidth}
                dataKey={xKey}
                type="category"
                tick={isEditCellSkeleton ? SkeletonTick : createCustomComparisonTick(size)}
              />
              {!isEditCellSkeleton && <Tooltip content={<CustomComparisonTooltip />} />}
              <Bar
                dataKey={barKey}
                barSize={30}
                shape={createCustomComparisonBar(
                  variant,
                  barsColor,
                  hoveredIndex,
                  setHoveredIndex,
                  isEditCellSkeleton
                )}
              />
              <ReferenceLine x={0} stroke="#79716B" strokeWidth={1} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
